import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { DumbbellIcon, MedalIcon, MessageIcon, TrophyIcon, UsersIcon, VideoIcon } from '../navIcons';
import { TemplatePuzzleGlyph } from '../ui/ActionGlyphs';
import ClipboardListIcon from '../ui/ClipboardIcon';
import { PAGE_X, PAGE_TOP } from '../student/videos/videoFormat';

const ReceiptIcon = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" fill="currentColor" {...props}>
    <path d="M328 112l-144 0-37.3-74.5c-1.8-3.6-2.7-7.6-2.7-11.6 0-14.3 11.6-25.9 25.9-25.9L342.1 0c14.3 0 25.9 11.6 25.9 25.9 0 4-.9 8-2.7 11.6L328 112zM169.6 160l172.8 0 48.7 40.6C457.6 256 496 338 496 424.5 496 472.8 456.8 512 408.5 512l-305.1 0C55.2 512 16 472.8 16 424.5 16 338 54.4 256 120.9 200.6L169.6 160zM260 224c-11 0-20 9-20 20l0 4c-28.8 .3-52 23.7-52 52.5 0 25.7 18.5 47.6 43.9 51.8l41.7 7c6 1 10.4 6.2 10.4 12.3 0 6.9-5.6 12.5-12.5 12.5L216 384c-11 0-20 9-20 20s9 20 20 20l24 0 0 4c0 11 9 20 20 20s20-9 20-20l0-4.7c25-4.1 44-25.7 44-51.8 0-25.7-18.5-47.6-43.9-51.8l-41.7-7c-6-1-10.4-6.2-10.4-12.3 0-6.9 5.6-12.5 12.5-12.5l47.5 0c11 0 20-9 20-20s-9-20-20-20l-8 0 0-4c0-11-9-20-20-20z" />
  </svg>
);

// Font Awesome solid bell, same glyph as the student header (StudentHeaderIcons).
const FaBellIcon = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" fill="currentColor" {...props}>
    <path d="M224 0c-17.7 0-32 14.3-32 32l0 3.2C119 50 64 114.6 64 192l0 21.7c0 48.1-16.4 94.8-46.4 132.4L7.8 358.3C2.7 364.6 0 372.4 0 380.5 0 400.1 15.9 416 35.5 416l376.9 0c19.6 0 35.5-15.9 35.5-35.5 0-8.1-2.7-15.9-7.8-22.2l-9.8-12.2C400.4 308.5 384 261.8 384 213.7l0-21.7c0-77.4-55-142-128-156.8l0-3.2c0-17.7-14.3-32-32-32zM162 464c7.1 27.6 32.2 48 62 48s54.9-20.4 62-48l-124 0z" />
  </svg>
);

/** Title + glyph of each coach screen; the glyph is the one its tab wears. */
const useCoachPageTitle = () => {
  const { pathname } = useLocation();
  const { t } = useTranslation('common');
  if (pathname.startsWith('/coach/exercises')) return { title: t('nav.exercises'), Icon: DumbbellIcon };
  if (pathname.startsWith('/coach/videotheque')) return { title: t('nav.video_library'), Icon: VideoIcon };
  if (pathname.startsWith('/coach/rankings')) return { title: t('nav.rankings'), Icon: TrophyIcon };
  if (pathname.startsWith('/coach/competitions')) return { title: t('nav.competitions'), Icon: MedalIcon };
  if (pathname.startsWith('/chat')) return { title: t('nav.messages'), Icon: MessageIcon };
  if (pathname.startsWith('/coach/facturation')) return { title: t('nav.billing'), Icon: ReceiptIcon };
  if (pathname.startsWith('/coach/questionnaires')) return { title: t('nav.questionnaires'), Icon: ClipboardListIcon };
  if (pathname.startsWith('/coach/templates')) return { title: t('coach_mobile.templates'), Icon: TemplatePuzzleGlyph };
  return { title: t('nav.clients'), Icon: UsersIcon };
};

/**
 * Coach header on phones, built like the student screens' own headers: page
 * title in copper with its tab's glyph on the left, bell + avatar on the
 * right. Replaces the desktop app bar below md only (MainLayout decides).
 *
 * Presentational: MainLayout's coach chrome leaf wires the bell to Header's
 * NotificationSidebar and the avatar to the coach profile sheet (props rather
 * than MainLayout's context, which would be an import cycle).
 */
const CoachMobileHeader = ({ onBellClick, onAvatarClick, hasUnread = false, hasAvatarDot = false }) => {
  const { t } = useTranslation('common');
  const { user } = useAuth();
  const { title, Icon } = useCoachPageTitle();
  const initial = (user?.name || user?.email || 'C').trim().charAt(0).toUpperCase();
  const circle = 'relative flex h-10 w-10 shrink-0 items-center justify-center rounded-full';

  return (
    <header
      data-testid="coach-mobile-header"
      className={`${PAGE_X} ${PAGE_TOP} relative z-20 flex shrink-0 items-center justify-between gap-[8px] pb-[12px]`}
    >
      <h1 className="flex min-w-0 items-center gap-[8px] text-[20px] font-medium text-kaiylo-accent">
        <Icon className="size-[20px] shrink-0" aria-hidden="true" />
        <span className="truncate">{title}</span>
      </h1>
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={onBellClick}
          aria-label={t('coach_mobile.notifications_aria')}
          className={`${circle} bg-white/10`}
        >
          <FaBellIcon className="h-[18px] w-[18px] text-kaiylo-txt" aria-hidden="true" />
          {hasUnread && (
            <span className="absolute right-[7px] top-[7px] h-2 w-2 rounded-full bg-kaiylo-accent ring-2 ring-[#2a2d37]" />
          )}
        </button>
        <button
          type="button"
          onClick={onAvatarClick}
          aria-label={t('coach_mobile.profile_aria')}
          className={`${circle} bg-kaiylo-accent text-[16px] font-semibold text-white`}
        >
          {initial}
          {hasAvatarDot && (
            <span className="absolute -right-[1px] -top-[1px] h-2.5 w-2.5 rounded-full bg-white ring-2 ring-[#2a2d37]" />
          )}
        </button>
      </div>
    </header>
  );
};

export default React.memo(CoachMobileHeader);
